import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Divider as _Divider } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';

import {
  CreativeFormatAspectRatio,
  CreativeFormatCreativeType,
  CreativeFormatNetwork,
  CreativeFormatPlacement,
  CreativeFormatPlaybackTime,
  Option,
} from '../../../graphql/generated/graphql';
import { CreationSet } from '../../../service/persistent/CreationSetWebApi';
import _CreativeBriefDetail from '../../component/compound/CreativeBriefDetail';
import _CreativeFormatDetail from '../../component/compound/CreativeFormatDetail';
import ProjectBasicSettingDetail from '../../component/compound/ProjectBasicSettingDetail';
import ProjectDesignInspiration from '../../component/compound/ProjectDesignInspiration';
import _ProjectTypeSummary from '../../component/compound/ProjectTypeSummary';
import { SPACING_UNIT } from '../../constant/spacing';

interface Props {
  project: CreationSet['project'];
  creativeFormat: {
    network: Pick<CreativeFormatNetwork, 'key' | 'name'>;
    placement: Pick<CreativeFormatPlacement, 'key' | 'name'>;
    creativeType: Pick<CreativeFormatCreativeType, 'key'>;
    attribute: {
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      playbackTime?: Pick<CreativeFormatPlaybackTime, 'seconds'> | null;
    };
    selectedPricingMerchandiseOptions: Array<Pick<Option, 'name' | 'displayToOptimizer' | 'helpUrlForOptimizer'>>;
  };
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<BucketId, FileReference[]>;
  className?: string;
}

const CreationSummary = ({
  project,
  creativeFormat,
  assetFiles,
  briefFiles,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const selectedPricingMerchandiseOptions: Array<Pick<Option, 'name' | 'displayToOptimizer' | 'helpUrlForOptimizer'>> =
    creativeFormat.selectedPricingMerchandiseOptions || [];

  return (
    <Root className={className}>
      <ProjectTypeSummary
        projectId={project.id}
        projectType={project.projectType}
        hasProjectChange={project.hasChanges}
      />
      <Divider />
      {project.designInspirationCase ? (
        <Fragment>
          <ProjectDesignInspiration designInspirationCase={project.designInspirationCase} />
          <Divider />
        </Fragment>
      ) : null}
      <CreativeFormatDetail
        creativeFormat={creativeFormat}
        regionKey={project.organization.businessRegion?.key}
        projectTypeKey={project.projectType.key}
      />
      <Divider />
      <ProjectBasicSettingDetail
        assetCategoryValue={project.assetCategoryValue}
        assetCategorySections={
          project.assetCategoryDefinition != null ? project.assetCategoryDefinition.sections : null
        }
        assetFiles={assetFiles}
        selectedPricingMerchandiseOptions={selectedPricingMerchandiseOptions}
      />
      <Divider />
      <CreativeBriefDetail
        definition={project.creativeBriefDefinition}
        value={project.creativeBriefValue}
        files={briefFiles}
      />
    </Root>
  );
};

const Root = styled('div')();

const Divider = styled(_Divider)({ marginBottom: SPACING_UNIT * 3 });

const ProjectTypeSummary = styled(_ProjectTypeSummary)({ marginBottom: SPACING_UNIT * 3 });

const CreativeFormatDetail = styled(_CreativeFormatDetail)({ marginTop: SPACING_UNIT * 3 });

const CreativeBriefDetail = styled(_CreativeBriefDetail)({ marginTop: SPACING_UNIT * 3 });

export default CreationSummary;
